import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import type { UpdateProgramCandidateParams } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import type { UpdateCandidateModalProps } from '@percent/admin-dashboard/app/grants/programs/grantsProgramDetails/programCandidates/updateCandidateModal/UpdateCandidateModal.types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ActionModal, Flex, FormField, Modal, Text, TextInput } from '@percent/lemonade'
import { useMutation, useQuery } from '@percent/utility'

type FormValues = {
  orgContactEmail: string
  orgContactFirstName: string
  orgContactLastName: string
  orgContactAccountId: string
}

const buildDiffPayload = (values: FormValues, initial: FormValues): Partial<FormValues> =>
  Object.fromEntries(Object.entries(values).filter(([key, value]) => value !== initial[key as keyof FormValues]))

function FormTextField({
  name,
  label,
  placeholder,
  testId
}: {
  name: keyof FormValues
  label: string
  placeholder: string
  testId: string
}) {
  return (
    <Field name={name}>
      {({ field, meta }: FieldProps<string>) => (
        <FormField
          label={label}
          status={meta.touched && meta.error ? 'danger' : 'default'}
          statusMessage={meta.touched && meta.error ? meta.error : undefined}
        >
          <TextInput
            data-testid={testId}
            placeholder={placeholder}
            name={field.name}
            value={field.value || ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            status={meta.touched && meta.error ? 'danger' : 'default'}
          />
        </FormField>
      )}
    </Field>
  )
}

export function UpdateCandidateModal({ candidateId, programId, isOpen, onClose, refresh }: UpdateCandidateModalProps) {
  const { t } = useTranslation()
  const { grantProgramService } = useServices()

  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)

  const {
    data: candidate,
    isLoading: isLoadingCandidate,
    refetch
  } = useQuery(
    ['/v1/admin/grants/candidates', candidateId],
    grantProgramService.getProgramCandidate,
    { candidateId },
    { enabled: Boolean(isOpen && candidateId) }
  )

  const [{ isPending: isSubmitting, errorMessage }, { apiFunc: updateCandidate }] = useMutation(
    grantProgramService.updateProgramCandidate,
    async () => {
      refresh()
      await refetch()
      setModalState('success')
    },
    () => {
      setModalState('error')
    }
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        orgContactEmail: Yup.string()
          .email(t('grants.programDetails.candidates.updateModal.validation.email.invalid'))
          .nullable()
          .optional(),
        orgContactFirstName: Yup.string().nullable().optional(),
        orgContactLastName: Yup.string().nullable().optional(),
        orgContactAccountId: Yup.string().nullable().optional()
      }),
    [t]
  )

  const initialValues = useMemo<FormValues>(
    () => ({
      orgContactEmail: candidate?.orgContactEmail ?? '',
      orgContactFirstName: candidate?.orgContactFirstName ?? '',
      orgContactLastName: candidate?.orgContactLastName ?? '',
      orgContactAccountId: candidate?.orgContactAccountId ?? ''
    }),
    [candidate]
  )

  const handleClose = useCallback(() => {
    onClose()
    setModalState(null)
  }, [onClose])

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const diff = buildDiffPayload(values, initialValues)

      if (Object.keys(diff).length === 0) {
        return
      }

      const payload: UpdateProgramCandidateParams = diff

      updateCandidate({
        programId,
        candidateId,
        payload
      })
    },
    [candidateId, programId, initialValues, updateCandidate]
  )

  const fields = [
    {
      name: 'orgContactEmail' as const,
      placeholder: 'email@example.org',
      testId: 'org-contact-email-input',
      label: t('grants.programDetails.candidates.updateModal.fields.orgContactEmail.label')
    },
    {
      name: 'orgContactFirstName' as const,
      placeholder: 'First name',
      testId: 'org-contact-first-name-input',
      label: t('grants.programDetails.candidates.updateModal.fields.orgContactFirstName.label')
    },
    {
      name: 'orgContactLastName' as const,
      placeholder: 'Last name',
      testId: 'org-contact-last-name-input',
      label: t('grants.programDetails.candidates.updateModal.fields.orgContactLastName.label')
    },
    {
      name: 'orgContactAccountId' as const,
      placeholder: 'Account ID',
      testId: 'org-contact-account-id-input',
      label: t('grants.programDetails.candidates.updateModal.fields.orgContactAccountId.label')
    }
  ]

  const renderModalContent = () => {
    if (modalState === 'success') {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('grants.programDetails.candidates.updateModal.success.title')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        >
          <Text size="small">{t('grants.programDetails.candidates.updateModal.success.text')}</Text>
        </AcknowledgeModal>
      )
    }

    if (modalState === 'error') {
      return (
        <AcknowledgeModal
          result="negative"
          title={t('grants.programDetails.candidates.updateModal.error.title')}
          description={errorMessage || t('grants.programDetails.candidates.updateModal.error.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      )
    }

    return (
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {formik => {
          const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues)

          return (
            <ActionModal
              title={t('grants.programDetails.candidates.updateModal.title')}
              primaryButtonText={t('button.save')}
              secondaryButtonText={t('button.cancel')}
              handleClose={handleClose}
              handleSubmit={formik.submitForm}
              loading={isSubmitting || isLoadingCandidate}
              disabled={!formik.isValid || !hasChanges || isSubmitting}
              maxHeight="80vh"
            >
              <Flex direction="column" gap="lg">
                {fields.map(f => (
                  <FormTextField key={f.name} {...f} />
                ))}
              </Flex>
            </ActionModal>
          )
        }}
      </Formik>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="update-candidate-modal">
      {renderModalContent()}
    </Modal>
  )
}
